import { reportBoundaryError } from '@hub/app/bootstrap/reportBoundaryError';
import { m } from '@hub/messages';
import { Button, StatusCard } from '@platform/ui';
import { useQueryClient } from '@tanstack/react-query';
import type { ErrorComponentProps } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useRouter } from '../hooks/useRouter';

// Inline route-level error: lives inside <Page> chrome (header, breadcrumbs,
// title and description from the route's staticData stay visible). Uses the
// shared error StatusCard pattern (eventId copy, dev stacktrace) — same as
// AsyncCard. The app-shell boundary in router.tsx keeps DefaultErrorComponent
// for the full-page UI.
export function RouteErrorComponent({ error, info }: ErrorComponentProps) {
  const queryClient = useQueryClient();
  const router = useRouter();
  const [eventId, setEventId] = useState('');

  useEffect(() => {
    setEventId(reportBoundaryError(error, 'router', info?.componentStack));
  }, [error, info?.componentStack]);

  // Mark queries stale without triggering refetches of unrelated active observers
  // (notifications, user, …); router.invalidate() then re-runs the failed match's
  // loader which actually refetches via prefetchQuery. The ErrorComponentProps
  // `reset` only clears local boundary state and would loop on the cached error.
  const handleRetry = () => {
    void queryClient.invalidateQueries({ refetchType: 'none' });
    void router.invalidate();
  };

  return (
    <StatusCard
      variant="error"
      title={m.commons_error_page_title()}
      description={m.commons_error_inline_message()}
      skeleton={null}
      error={error}
      eventId={eventId}
      copyLabel={m.commons_error_page_copy_error_id()}
      copiedLabel={m.commons_error_page_copied_error_id()}
      copyFailedLabel={m.commons_error_page_copy_failed()}
      action={<Button onClick={handleRetry}>{m.commons_error_page_try_again()}</Button>}
    />
  );
}
